import hotkeys from 'hotkeys-js';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {Canvas, FabricImage, Point, Rect, util} from '@postermywall/fabricjs-2';
import type {FabricObject, TMat2D, TPointerEventInfo} from '@postermywall/fabricjs-2';
import {loadImageAsync} from '@Utils/image.util';
import {KeyboardShortcutScope} from '@Libraries/keyboard-shortcuts';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {degreesToRadians, positiveModulo} from '@Utils/math.util';
import clamp from 'lodash/clamp';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {GROWL_TYPE} from '@Libraries/message-growl.types';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {trackCropClicked} from '@Libraries/poster-editor-tracking.library';
import type {ItemCropObject} from '@PosterWhiteboard/classes/item-crop.class';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {
  CropAspectLock,
  CropFrame,
  CropFrames,
  CropImageCorner,
  CropSourceElement,
  CropTargetItem,
  StraightenCropState,
} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.types';
import {
  CROP_ASPECT_RATIO_MATCH_TOLERANCE,
  CROP_CONTAINMENT_SEARCH_STEPS,
  CROP_FRAME_EQUALITY_TOLERANCE,
  CROP_GA_PRESET_BY_ASPECT_RATIO,
  CROP_GA_PRESET_FREE,
  CROP_GA_PRESET_ORIGINAL,
  CROP_GA_PRESET_PAGE,
  CROP_SCRIM_COLOR,
  CROP_SCRIM_OPACITY,
  CROP_NOTICE_GROWL_INTERVAL_MS,
  CROP_OUTSIDE_CLICK_TOLERANCE_PX,
  CROP_SURFACE_ATTRIBUTE,
  CropHandle,
  CropMediaType,
  MIN_CROP_SIZE_PX,
} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.types';
import {
  clampWindowCentre,
  fitWindowInsideSource,
  getPictureFrame,
  getPictureMatrix,
  getWindowFrame,
  isWindowInsideSource,
  sceneToSource,
  sceneToWindowLocal,
  sceneVectorToSource,
  sourceToScene,
  windowLocalToScene,
} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop-geometry';
import {renderCropThirdsGuides} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop-guides';
import {getPointerClientPoint} from '@PosterWhiteboard/libraries/pointer-client-point.library';

/** The edges of the crop window in its own frame: origin at the window centre, scene units. */
interface WindowEdges {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * Fabric normalizes `angle` into 0..360, but the rotation slider works in -180..180 - so a
 * 350deg item has to read as -10deg for the slider to land on the item's actual rotation.
 */
const toSignedAngle = (angle: number): number => {
  return positiveModulo(angle + 180, 360) - 180;
};

/** `isVideo()` is keyed on the item type, so a slideshow's video slide needs its own guard alongside it. */
const isVideoTarget = (item: CropTargetItem): item is VideoItem | VideoSlideItem => {
  return item.isVideo() || item.isVideoSlide();
};

/** A video's intrinsic size lives on videoWidth/videoHeight; an image's on width/height once loaded. */
const getSourceSize = (source: CropSourceElement): {width: number; height: number} => {
  if (source instanceof HTMLVideoElement) {
    return {width: source.videoWidth, height: source.videoHeight};
  }
  return {width: source.width, height: source.height};
};

const isSameFrame = (a: CropFrame, b: CropFrame, tolerance = 0): boolean => {
  return (
    Math.abs(a.left - b.left) <= tolerance &&
    Math.abs(a.top - b.top) <= tolerance &&
    Math.abs(a.width - b.width) <= tolerance &&
    Math.abs(a.height - b.height) <= tolerance &&
    Math.abs(a.angle - b.angle) <= tolerance
  );
};

const areFramesEqual = (a: CropFrames, b: CropFrames | null, tolerance = 0): boolean => {
  return b !== null && isSameFrame(a.window, b.window, tolerance) && isSameFrame(a.image, b.image, tolerance);
};

const isLeftHandle = (handle: CropHandle): boolean => {
  return handle === CropHandle.TOP_LEFT || handle === CropHandle.BOTTOM_LEFT || handle === CropHandle.MIDDLE_LEFT;
};
const isRightHandle = (handle: CropHandle): boolean => {
  return handle === CropHandle.TOP_RIGHT || handle === CropHandle.BOTTOM_RIGHT || handle === CropHandle.MIDDLE_RIGHT;
};
const isTopHandle = (handle: CropHandle): boolean => {
  return handle === CropHandle.TOP_LEFT || handle === CropHandle.TOP_RIGHT || handle === CropHandle.MIDDLE_TOP;
};
const isBottomHandle = (handle: CropHandle): boolean => {
  return handle === CropHandle.BOTTOM_LEFT || handle === CropHandle.BOTTOM_RIGHT || handle === CropHandle.MIDDLE_BOTTOM;
};

/**
 * On-canvas cropping. The crop window is a transparent rectangle on the canvas - the active object,
 * so it drags - over the whole picture at full strength, with a black scrim darkening everything of
 * the picture outside the window. Rotation turns the picture about the window's centre while the
 * window stays put, from the slider or the picture's own rotation handle; the item is re-baked with
 * the resulting `cropData`, rotation included, only on apply.
 *
 * For a page background the window is the page itself ({@link isWindowFixed}): it cannot be resized
 * or dragged, a press on it pans the picture, and only the picture's own scale and rotation handles
 * remain.
 */
export class PosterMediaCrop {
  private activeItem: CropTargetItem | null = null;
  private aspectRatio: number | null = null;
  /** "Original" locked the ratio; a square picture's own ratio is also Square's, so the value alone cannot say which preset is on. */
  private isOriginalAspectRatio = false;
  /** The preset each item was last cropped with, so reopening its crop in this session starts from it. Never stored with the poster. */
  private readonly lastAspectLockByItemUid = new Map<string, CropAspectLock>();
  /** An apply is in flight until its re-init lands; a second apply or a cancel meanwhile would tear the mode down under it. */
  private isApplying = false;
  private state: StraightenCropState | null = null;
  private windowRect: Rect | null = null;
  private windowClip: Rect | null = null;
  private pictureImage: FabricImage | null = null;
  /** Darkens the picture outside the window; the window clip cuts the hole. */
  private scrimRect: Rect | null = null;
  /** The item's own fabric object, hidden on entry and shown again on exit. */
  private hiddenSourceObject: FabricObject | null = null;
  private mediaType = CropMediaType.IMAGE;
  private sourceFlipX = false;
  private sourceFlipY = false;
  private panStartScene = new Point(0, 0);
  private panStartWindowCentre = new Point(0, 0);
  private isPanning = false;
  /** Where a press landed outside the crop area, in client pixels; its release, if it has not travelled, is a click that leaves crop mode. */
  private outsidePressClientPoint: Point | null = null;
  /** Both boxes as they stood on entry, so a click-out can tell an edited crop from an untouched one. */
  private entryFrames: CropFrames | null = null;
  /** A picture handle gesture has been reported as scaling or rotating and is not yet released. */
  private isPictureGestureReported = false;
  /**
   * The window is the page: a background is being cropped. The window cannot be resized or moved;
   * the picture pans, scales and turns behind it, and the page keeps its own shape as the ratio.
   */
  private isWindowFixed = false;
  private previousCanvasSelection = true;
  private lastPublishedFrames: CropFrames | null = null;
  /** Restored on exit; every editor shortcut is parked while the crop scope is active. */
  private previousShortcutScope: string = KeyboardShortcutScope.POSTER_EDITOR;

  public isActive(): boolean {
    return this.activeItem !== null;
  }

  public getActiveItem(): CropTargetItem | null {
    return this.activeItem;
  }

  public async enterCropMode(item: CropTargetItem): Promise<void> {
    // Refine may still be loading its sources; two on-canvas modes would each freeze the pages and fight over what to unfreeze.
    if (item.page.poster.isOnCanvasEditModeActive()) {
      return;
    }
    if (!isVideoTarget(item) && (item.isDrawing() || item.isIcon())) {
      return;
    }

    this.mediaType = isVideoTarget(item) ? CropMediaType.VIDEO : CropMediaType.IMAGE;
    trackCropClicked(this.mediaType);

    // Reserved before the picture downloads so a second crop request cannot start mid-load; released
    // again below if the download fails or the mode is cancelled while waiting.
    this.activeItem = item;
    this.aspectRatio = null;
    this.isOriginalAspectRatio = false;
    this.isWindowFixed = !item.isMediaSlide() && item.isBackground;

    const fullSource = await this.#loadFullSource(item);
    if (this.activeItem !== item) {
      return;
    }
    if (!fullSource) {
      this.activeItem = null;
      getPosterUi().showMessageGrowl({
        text: window.i18next.t('pmwjs_something_went_wrong'),
        type: GROWL_TYPE.DANGER,
        interval: CROP_NOTICE_GROWL_INTERVAL_MS,
        showCloseIcon: true,
      });
      return;
    }

    const page = item.page;
    // A click on the canvas pauses playback before any edit; crop mode is entered from controls too, so it pauses itself.
    if (page.poster.isPlaying()) {
      await page.poster.pause();
    }
    const source = item.fabricObject;
    this.sourceFlipX = source.flipX;
    this.sourceFlipY = source.flipY;
    // A stored crop was rounded to whole pixels on apply, so a straightened one can sit a hair outside the picture.
    const stateFromItem = this.#stateFromItem(item, getSourceSize(fullSource));
    this.state = fitWindowInsideSource(this.isWindowFixed ? this.#ensureWindowIsPageShaped(stateFromItem, page) : stateFromItem);
    if (this.isWindowFixed) {
      this.aspectRatio = page.poster.getAspectRatio();
    }

    page.clearSelection();
    page.poster.pages.setItemsInteractiveOnEveryPage(false);
    // The window is not an item; the canvas centring, edge and alignment lines must not follow it.
    page.renderAlignmentGuidelines.suspend();
    source.visible = false;
    this.hiddenSourceObject = source;

    this.#createCanvasObjects(page, fullSource, source);
    this.#syncCanvasObjects();
    if (this.windowRect) {
      page.activeSelection.setActiveObject(this.windowRect);
    }
    page.fabricCanvas.requestRenderAll();
    window.addEventListener('pointerdown', this.onDocumentPointerDown, true);
    window.addEventListener('pointerup', this.onDocumentPointerUp, true);
    window.addEventListener('pointercancel', this.onDocumentPointerCancel, true);
    this.previousShortcutScope = hotkeys.getScope();
    hotkeys.setScope(KeyboardShortcutScope.MEDIA_CROP);

    getPosterUi().enterMediaCropMode({
      itemId: item.uid,
      pageHashedId: page.hashedID,
      aspectRatio: this.aspectRatio,
      originalAspectRatio: this.state.sourceHeight > 0 ? this.state.sourceWidth / this.state.sourceHeight : null,
      rotation: this.state.pictureRotation,
      isWindowFixed: this.isWindowFixed,
    });
    this.#restoreRememberedAspectLock(item);
    // Taken after the remembered preset has refitted the window, so opening and clicking straight out is not an edit.
    this.entryFrames = this.#computeFrames();

    // Mobile has no side panel, so the options live in a non-blocking bottom sheet. Opened here
    // rather than at each crop entry point so the tap, the shortcut and a double-tap all get it.
    if (isEditorMobileVariant()) {
      getPosterUi().openPosterEditorCropModal();
    }
  }

  /**
   * Locks the window to a ratio - the largest window of that ratio the picture can hold, edge to edge,
   * centred where the window stood and kept inside the picture - or frees it again with `null`. The
   * picture never moves; a turned picture holds less, so the window shrinks about that centre until it
   * fits. The dragged axis stays authoritative on later handle drags.
   */
  public setAspectRatio(ratio: number | null): void {
    if (this.isWindowFixed) {
      return;
    }
    this.#lockAspectRatio({ratio, isOriginal: false});
    const state = this.state;
    if (!state || ratio === null) {
      return;
    }
    const filled = this.#stateFilledToRatio(state, ratio);
    const edges = this.#currentEdges(filled);
    const scaledAboutCentre = (fraction: number): WindowEdges => {
      return {left: edges.left * fraction, right: edges.right * fraction, top: edges.top * fraction, bottom: edges.bottom * fraction};
    };
    this.#applyEdges(filled, scaledAboutCentre, this.#largestContainedFraction((fraction) => this.#stateWithEdges(filled, scaledAboutCentre(fraction))));
  }

  /** The window at the largest size of `ratio` the upright picture can hold, its centre moved the least to stay inside. */
  #stateFilledToRatio(state: StraightenCropState, ratio: number): StraightenCropState {
    const windowWidth = Math.min(state.sourceWidth, state.sourceHeight * ratio);
    const filled: StraightenCropState = {...state, windowWidth, windowHeight: windowWidth / ratio};
    const windowCentre = clampWindowCentre(filled);
    return {...filled, windowCentre, windowCentreScene: sourceToScene(state, windowCentre)};
  }

  /**
   * The "Original" preset: the whole picture back in the window as it stood before any crop - no cut,
   * no turn - with the ratio then locked to the picture's own. The picture keeps its place on screen;
   * it is un-turned about the window's centre and the window grows to its bounds.
   */
  public resetToOriginal(): void {
    const state = this.state;
    if (!state) {
      return;
    }
    getPosterUi().setMediaCropRotation(0);
    const upright: StraightenCropState = {...state, pictureRotation: 0};
    const pictureCentre = new Point(state.sourceWidth / 2, state.sourceHeight / 2);
    if (this.isWindowFixed) {
      this.#resetPictureUnderPage(upright, pictureCentre);
      return;
    }
    const originalRatio = state.sourceHeight > 0 ? state.sourceWidth / state.sourceHeight : null;
    this.#lockAspectRatio({ratio: originalRatio, isOriginal: true});
    this.state = {
      ...upright,
      windowCentre: pictureCentre,
      windowCentreScene: sourceToScene(upright, pictureCentre),
      windowWidth: state.sourceWidth,
      windowHeight: state.sourceHeight,
    };
    this.#syncCanvasObjects();
  }

  /** A background's Original: the picture upright and centred, showing as much of itself as the page's shape allows. */
  #resetPictureUnderPage(upright: StraightenCropState, pictureCentre: Point): void {
    const page = this.activeItem?.page;
    if (!page) {
      return;
    }
    this.state = this.#fillWindowToPage({...upright, windowCentre: pictureCentre}, page);
    this.#syncCanvasObjects();
  }

  /**
   * A background enters with the page as its window. A stored page-shaped crop is kept as it stands;
   * a background that was stretched to fit the page instead gets a page-shaped window about its centre.
   */
  #ensureWindowIsPageShaped(state: StraightenCropState, page: Page): StraightenCropState {
    return this.#hasWindowRatio(state, page.poster.getAspectRatio()) ? state : this.#fillWindowToPage(state, page);
  }

  /**
   * The largest page-shaped window the picture holds about the state's window centre, scaled so it lands
   * exactly on the page: the window's on-screen box is the page whatever the picture's own shape.
   */
  #fillWindowToPage(state: StraightenCropState, page: Page): StraightenCropState {
    const displayWidth = state.windowWidth * state.scaleX;
    const displayHeight = state.windowHeight * state.scaleY;
    const filled = this.#stateFilledToRatio(state, page.poster.getAspectRatio());
    return {...filled, scaleX: displayWidth / filled.windowWidth, scaleY: displayHeight / filled.windowHeight};
  }

  /**
   * Turns the picture about the window's centre while the window stays put. The picture zooms in as
   * far as the turn needs so no corner of the window is left uncovered; it does not zoom back out on
   * its own - the picture's corner handles do that.
   */
  public setRotation(degrees: number): void {
    getPosterUi().setMediaCropRotation(degrees);
    const state = this.state;
    if (!state) {
      return;
    }
    this.state = fitWindowInsideSource({...state, pictureRotation: degrees});
    this.#syncCanvasObjects();
  }

  /** The picture's rotation handle: the turn, reported as a rotation so the editor's angle readout follows the pointer as it does for an item. */
  public rotatePictureFromHandle(degrees: number): void {
    this.setRotation(degrees);
    this.#reportPictureGesture('rotating');
  }

  /**
   * Drags one corner of the picture to a scene point, scaling it uniformly about the opposite corner
   * while the window stays exactly where it is on screen - dragging outward zooms in, inward zooms
   * out. The picture can never be pulled away from the window.
   */
  public scaleImageFromCorner(corner: CropImageCorner, scenePoint: Point): void {
    const state = this.state;
    if (!state) {
      return;
    }
    const frame = getPictureFrame(state);
    const isLeft = corner === CropHandle.TOP_LEFT || corner === CropHandle.BOTTOM_LEFT;
    const isTop = corner === CropHandle.TOP_LEFT || corner === CropHandle.TOP_RIGHT;
    const anchor = sourceToScene(state, new Point(isLeft ? state.sourceWidth : 0, isTop ? state.sourceHeight : 0));
    const towardsPointer = scenePoint.subtract(anchor).rotate(-degreesToRadians(frame.angle));
    const wantedFactor = Math.max(Math.abs(towardsPointer.x) / frame.width, Math.abs(towardsPointer.y) / frame.height);
    if (!Number.isFinite(wantedFactor) || wantedFactor <= 0) {
      return;
    }
    const scaledBy = (fraction: number): StraightenCropState => {
      return this.#pictureScaledAbout(state, anchor, 1 + (wantedFactor - 1) * fraction);
    };
    const fraction = this.#largestContainedFraction(scaledBy);
    if (fraction === 0) {
      return;
    }
    this.state = scaledBy(fraction);
    this.#syncCanvasObjects();
    this.#reportPictureGesture('scaling');
  }

  /**
   * The picture stands in for the item while it is cropped, so its handle gestures are reported the way
   * fabric reports an item's: the editor's size and angle readout then shows for them as for any item.
   */
  #reportPictureGesture(gesture: 'scaling' | 'rotating'): void {
    this.activeItem?.page.setPosterItemsModificationState(gesture);
    this.isPictureGestureReported = true;
  }

  #releasePictureGesture(page: Page): void {
    if (!this.isPictureGestureReported) {
      return;
    }
    this.isPictureGestureReported = false;
    page.setPosterItemsModificationState(false);
  }

  /**
   * Drags one handle of the crop window to a scene point. The picture stays pinned on screen and only
   * the window changes, which is what makes a crop read as a crop rather than a move. Edges move along
   * the window's own axes, so this holds at any item angle, and stop where the window would leave the
   * picture or drop below {@link MIN_CROP_SIZE_PX}.
   */
  public resizeCropFromHandle(handle: CropHandle, scenePoint: Point): void {
    const state = this.state;
    if (!state || this.isWindowFixed) {
      return;
    }
    const edges = this.#currentEdges(state);
    const target = this.#edgesDraggedTo(state, edges, handle, sceneToWindowLocal(state, scenePoint));
    const reached = this.aspectRatio === null ? this.#edgesReachedAxisByAxis(state, edges, target) : this.#edgesReachedTogether(state, edges, target);
    this.state = this.#stateWithEdges(state, reached);
    this.#syncCanvasObjects();
  }

  /**
   * A free drag settles each axis on its own: an edge stopped at the picture's border does not hold
   * the other edge back, so a pointer that has left the picture to one side still drives the window
   * along the other axis. The horizontal move settles first, the vertical one against its result.
   */
  #edgesReachedAxisByAxis(state: StraightenCropState, from: WindowEdges, to: WindowEdges): WindowEdges {
    const horizontal = this.#edgesReachedTogether(state, from, {...from, left: to.left, right: to.right});
    return this.#edgesReachedTogether(state, horizontal, {...horizontal, top: to.top, bottom: to.bottom});
  }

  /** The edges at the largest fraction of the move from `from` to `to` that keeps the window on the picture; `from` when it cannot start. */
  #edgesReachedTogether(state: StraightenCropState, from: WindowEdges, to: WindowEdges): WindowEdges {
    const between = (fraction: number): WindowEdges => {
      return {
        left: from.left + (to.left - from.left) * fraction,
        right: from.right + (to.right - from.right) * fraction,
        top: from.top + (to.top - from.top) * fraction,
        bottom: from.bottom + (to.bottom - from.bottom) * fraction,
      };
    };
    return between(this.#largestContainedFraction((fraction) => this.#stateWithEdges(state, between(fraction))));
  }

  public async applyCrop(): Promise<void> {
    const item = this.activeItem;
    const state = this.state;
    const windowRect = this.windowRect;
    if (!item || !state || !windowRect || this.isApplying) {
      return;
    }
    this.isApplying = true;

    const cropData = this.#cropDataFromState(state);
    this.#trackApplyGA();
    const page = item.page;

    // The canvas objects stay up and keep covering the item until the destructive re-init completes,
    // so the pre-crop image never flashes; the re-init preserves the placement passed here, landing
    // the item directly on the window with no intermediate frame.
    try {
      if (this.isWindowFixed) {
        // A background is laid out by the page itself, so it takes only the crop and refits full-bleed.
        await item.updateFromObject({cropData});
      } else if (item.isMediaSlide()) {
        // The slideshow lays its slides out itself, so a slide takes only the crop; its place and size
        // follow the slideshow frame.
        await item.slideshow.updateSlideFromObject(item.uid, {cropData});
      } else {
        await item.updateFromObject({
          cropData,
          left: windowRect.left,
          top: windowRect.top,
          angle: state.windowAngle,
          scaleX: state.scaleX,
          scaleY: state.scaleY,
        });
      }
    } finally {
      this.isApplying = false;
    }

    this.#teardownCanvasObjects(page);
    this.#finishExit(page, item);
  }

  public cancelCrop(): void {
    const item = this.activeItem;
    if (!item || this.isApplying) {
      return;
    }
    if (!this.state) {
      // Still downloading the picture: nothing is set up yet, so releasing the reservation is the whole exit.
      this.activeItem = null;
      return;
    }
    const page = item.page;
    this.#teardownCanvasObjects(page);
    this.#finishExit(page, item);
  }

  /**
   * A video item's own <video> already holds the full picture, so the edit image draws its current
   * frame; an image is downloaded at full size, with any background-removal refinement painted in so
   * the crop shows what the item shows. Resolves to null instead of throwing, so a failed download can
   * never leave the mode half-entered.
   */
  async #loadFullSource(item: CropTargetItem): Promise<CropSourceElement | null> {
    if (isVideoTarget(item)) {
      return item.htmlElement;
    }
    try {
      return await item.refinement.applyRefinementToImage(await loadImageAsync(await item.getImageUrl()));
    } catch {
      return null;
    }
  }

  /**
   * The crop as the item holds it, mapped onto the loaded source. A slide's own transform is relative
   * to its slideshow group, so the full transform is decomposed; the item's flips are cancelled out
   * first, because a mirrored matrix reads as a half-turn with a negative scale.
   */
  #stateFromItem(item: CropTargetItem, sourceSize: {width: number; height: number}): StraightenCropState {
    const source = item.fabricObject;
    const flipSign = util.createScaleMatrix(source.flipX ? -1 : 1, source.flipY ? -1 : 1);
    const transform = util.qrDecompose(util.multiplyTransformMatrices(source.calcTransformMatrix(), flipSign));
    // The content box only: the scaled dimensions would count a solid border's stroke as picture.
    const displayWidth = source.width * transform.scaleX;
    const displayHeight = source.height * transform.scaleY;

    const crop = item.cropData;
    const hasCrop = crop.cropped && crop.imageWidth > 0 && crop.imageHeight > 0;
    const toSourceX = hasCrop ? sourceSize.width / crop.imageWidth : 1;
    const toSourceY = hasCrop ? sourceSize.height / crop.imageHeight : 1;
    const windowWidth = hasCrop ? crop.width * toSourceX : sourceSize.width;
    const windowHeight = hasCrop ? crop.height * toSourceY : sourceSize.height;
    const elementCentre = hasCrop
      ? new Point((crop.x + crop.width / 2) * toSourceX, (crop.y + crop.height / 2) * toSourceY)
      : new Point(sourceSize.width / 2, sourceSize.height / 2);

    return {
      sourceWidth: sourceSize.width,
      sourceHeight: sourceSize.height,
      windowCentre: this.#toVisualPoint(elementCentre, sourceSize),
      windowWidth,
      windowHeight,
      pictureRotation: this.#toVisualRotation(hasCrop ? crop.rotation : 0),
      windowCentreScene: new Point(transform.translateX, transform.translateY),
      windowAngle: toSignedAngle(transform.angle),
      scaleX: displayWidth / windowWidth,
      scaleY: displayHeight / windowHeight,
    };
  }

  /** The stored crop is in element space; the item's flips mirror it into what the user sees, and back. */
  #toVisualPoint(elementPoint: Point, sourceSize: {width: number; height: number}): Point {
    return new Point(this.sourceFlipX ? sourceSize.width - elementPoint.x : elementPoint.x, this.sourceFlipY ? sourceSize.height - elementPoint.y : elementPoint.y);
  }

  /** A mirror reverses the sense of a turn; two mirrors cancel. The mapping is its own inverse. */
  #toVisualRotation(rotation: number): number {
    return this.sourceFlipX !== this.sourceFlipY ? -rotation : rotation;
  }

  /** Whole pixels that never reach past the picture: the bake would fill any overshoot with transparency. */
  #cropDataFromState(state: StraightenCropState): ItemCropObject {
    const sourceSize = {width: state.sourceWidth, height: state.sourceHeight};
    const elementCentre = this.#toVisualPoint(state.windowCentre, sourceSize);
    const width = Math.floor(state.windowWidth);
    const height = Math.floor(state.windowHeight);
    return {
      cropped: true,
      x: clamp(Math.round(elementCentre.x - width / 2), 0, state.sourceWidth - width),
      y: clamp(Math.round(elementCentre.y - height / 2), 0, state.sourceHeight - height),
      width,
      height,
      imageWidth: state.sourceWidth,
      imageHeight: state.sourceHeight,
      rotation: this.#toVisualRotation(state.pictureRotation),
    };
  }

  #createCanvasObjects(page: Page, fullSource: CropSourceElement, source: FabricObject): void {
    // Inverted, the clip keeps the scrim everywhere but the window, so the window shows the picture bare.
    const windowClip = new Rect({absolutePositioned: true, inverted: true, originX: 'center', originY: 'center', strokeWidth: 0, objectCaching: false});
    const pictureOptions = {selectable: false, evented: false, objectCaching: false, strokeWidth: 0};
    const pictureImage = new FabricImage(fullSource, pictureOptions);
    const scrimRect = new Rect({...pictureOptions, fill: CROP_SCRIM_COLOR, opacity: CROP_SCRIM_OPACITY, clipPath: windowClip});
    this.#applyItemFilters(source, pictureImage);
    const windowRect = new Rect({
      fill: 'transparent',
      strokeWidth: 0,
      padding: 0,
      // A fixed window stays the active object - the editor's size and angle readout keys off one - but
      // fabric may not move it; a press on it pans the picture instead (see onCanvasMouseDown).
      lockMovementX: this.isWindowFixed,
      lockMovementY: this.isWindowFixed,
      hasControls: false,
      hasBorders: false,
      lockScalingX: true,
      lockScalingY: true,
      // The window never turns in crop mode; the picture does, by its own handle and the slider.
      lockRotation: true,
      objectCaching: false,
      // The editor's objects hit-test per pixel; a transparent rectangle has none, so it takes its box.
      perPixelTargetFind: false,
    });
    // The guides must paint inside the window, and fabric fires `after:render` on the canvas only -
    // never per object - so patching the rectangle's own paint hook is the seam that lands on top.
    // No unpatch is needed: the rectangle is discarded on exit rather than handed back to the poster.
    // eslint-disable-next-line no-underscore-dangle -- fabric's per-object paint hook, as in ItemRenderDecorations
    windowRect._render = (ctx: CanvasRenderingContext2D): void => {
      renderCropThirdsGuides({width: windowRect.width, height: windowRect.height}, page.fabricCanvas.getZoom(), ctx);
    };
    windowRect.on('moving', this.onWindowMoving);

    this.windowClip = windowClip;
    this.pictureImage = pictureImage;
    this.scrimRect = scrimRect;
    this.windowRect = windowRect;

    if (page.fabricCanvas instanceof Canvas) {
      this.previousCanvasSelection = page.fabricCanvas.selection;
      page.fabricCanvas.selection = false;
      page.fabricCanvas.on('mouse:down', this.onCanvasMouseDown);
      page.fabricCanvas.on('mouse:move', this.onCanvasMouseMove);
      page.fabricCanvas.on('mouse:up', this.onCanvasMouseUp);
    }
    page.fabricCanvas.on('after:render', this.onAfterRender);
    if (this.isWindowFixed) {
      // The design's own content stays in view above the picture while the background is framed under it.
      page.fabricCanvas.insertAt(page.fabricCanvas.getObjects().indexOf(source) + 1, pictureImage, scrimRect, windowRect);
      return;
    }
    page.fabricCanvas.add(pictureImage, scrimRect, windowRect);
  }

  /**
   * The picture keeps the item's colour effects while it is being cropped: the item's fabric image
   * already holds its filter stack in the item's own order, so the picture takes a copy of it.
   * Borders, edge shapes and masks are cut-dependent - they wrap the cropped result - so they wait.
   */
  #applyItemFilters(source: FabricObject, picture: FabricImage): void {
    if (!(source instanceof FabricImage) || source.filters.length === 0) {
      return;
    }
    picture.filters = [...source.filters];
    picture.applyFilters();
  }

  /**
   * Lays the canvas objects out from the state. The window rectangle and its clip share one
   * placement; the picture and its scrim take the picture matrix with the element's own mirror folded back in,
   * decomposed into what a fabric object can hold (a negative scale becomes a flip).
   */
  #syncCanvasObjects(): void {
    const state = this.state;
    const {windowRect, windowClip, pictureImage, scrimRect} = this;
    if (!state || !windowRect || !windowClip || !pictureImage || !scrimRect) {
      return;
    }
    scrimRect.set({width: state.sourceWidth, height: state.sourceHeight});
    const windowScreenWidth = state.windowWidth * state.scaleX;
    const windowScreenHeight = state.windowHeight * state.scaleY;
    for (const rect of [windowRect, windowClip]) {
      rect.set({width: windowScreenWidth, height: windowScreenHeight, angle: state.windowAngle});
      rect.setPositionByOrigin(state.windowCentreScene, 'center', 'center');
      rect.setCoords();
    }

    const objectMatrix: TMat2D = util.multiplyTransformMatrixArray([
      getPictureMatrix(state),
      util.createTranslateMatrix(state.sourceWidth / 2, state.sourceHeight / 2),
      util.createScaleMatrix(this.sourceFlipX ? -1 : 1, this.sourceFlipY ? -1 : 1),
    ]);
    const placement = util.qrDecompose(objectMatrix);
    for (const object of [pictureImage, scrimRect]) {
      object.set({flipX: false, flipY: false, angle: placement.angle, scaleX: placement.scaleX, scaleY: placement.scaleY, skewX: placement.skewX, skewY: placement.skewY});
      object.setPositionByOrigin(new Point(placement.translateX, placement.translateY), 'center', 'center');
      object.setCoords();
    }
    this.activeItem?.page.fabricCanvas.requestRenderAll();
  }

  #currentEdges(state: StraightenCropState): WindowEdges {
    const halfWidth = (state.windowWidth * state.scaleX) / 2;
    const halfHeight = (state.windowHeight * state.scaleY) / 2;
    return {left: -halfWidth, right: halfWidth, top: -halfHeight, bottom: halfHeight};
  }

  /**
   * The edges the pointer asks for, in the window's frame: the handle's own edges follow the pointer,
   * never past the opposite edge minus the minimum size, and with a ratio locked the dragged axis
   * decides the other - an edge handle has only its own axis to offer.
   */
  #edgesDraggedTo(state: StraightenCropState, edges: WindowEdges, handle: CropHandle, pointer: Point): WindowEdges {
    const minWidth = MIN_CROP_SIZE_PX * state.scaleX;
    const minHeight = MIN_CROP_SIZE_PX * state.scaleY;
    const target = {...edges};
    if (isLeftHandle(handle)) {
      target.left = Math.min(pointer.x, edges.right - minWidth);
    }
    if (isRightHandle(handle)) {
      target.right = Math.max(pointer.x, edges.left + minWidth);
    }
    if (isTopHandle(handle)) {
      target.top = Math.min(pointer.y, edges.bottom - minHeight);
    }
    if (isBottomHandle(handle)) {
      target.bottom = Math.max(pointer.y, edges.top + minHeight);
    }
    if (this.aspectRatio === null) {
      return target;
    }
    const ratioOnScreen = (this.aspectRatio * state.scaleX) / state.scaleY;
    const widthLeads = handle !== CropHandle.MIDDLE_TOP && handle !== CropHandle.MIDDLE_BOTTOM;
    const width = widthLeads ? target.right - target.left : (target.bottom - target.top) * ratioOnScreen;
    const height = widthLeads ? width / ratioOnScreen : target.bottom - target.top;
    if (isLeftHandle(handle)) {
      target.left = target.right - width;
    } else {
      target.right = target.left + width;
    }
    if (isTopHandle(handle)) {
      target.top = target.bottom - height;
    } else {
      target.bottom = target.top + height;
    }
    return target;
  }

  /** Rebuilds the window from edges in the frame of `state`'s window; the picture stays pinned on screen. */
  #stateWithEdges(state: StraightenCropState, edges: WindowEdges): StraightenCropState {
    const centreLocal = new Point((edges.left + edges.right) / 2, (edges.top + edges.bottom) / 2);
    const windowCentreScene = windowLocalToScene(state, centreLocal);
    return {
      ...state,
      windowCentreScene,
      windowCentre: sceneToSource(state, windowCentreScene),
      windowWidth: (edges.right - edges.left) / state.scaleX,
      windowHeight: (edges.bottom - edges.top) / state.scaleY,
    };
  }

  /** Moves the window to the edges at `fraction` of a gesture, in the frame of `state`'s window; 0 means the gesture could not start. */
  #applyEdges(state: StraightenCropState, edgesAt: (fraction: number) => WindowEdges, fraction: number): void {
    if (fraction === 0) {
      return;
    }
    this.state = this.#stateWithEdges(state, edgesAt(fraction));
    this.#syncCanvasObjects();
  }

  /** The state after the picture is scaled by `factor` about a scene point, the window fixed on screen. */
  #pictureScaledAbout(state: StraightenCropState, anchor: Point, factor: number): StraightenCropState {
    const scaledMatrix = util.multiplyTransformMatrixArray([
      util.createTranslateMatrix(anchor.x, anchor.y),
      util.createScaleMatrix(factor, factor),
      util.createTranslateMatrix(-anchor.x, -anchor.y),
      getPictureMatrix(state),
    ]);
    return {
      ...state,
      scaleX: state.scaleX * factor,
      scaleY: state.scaleY * factor,
      windowWidth: state.windowWidth / factor,
      windowHeight: state.windowHeight / factor,
      windowCentre: state.windowCentreScene.transform(util.invertTransform(scaledMatrix)),
    };
  }

  /**
   * The largest fraction of a gesture that keeps the window on the picture, found by halving:
   * `build(1)` is the gesture in full and `build(0)` the current, contained state. Containment is
   * monotonic along one gesture, so the answer is exact to 1 / 2^steps of it; 0 means it cannot start.
   */
  #largestContainedFraction(build: (fraction: number) => StraightenCropState): number {
    if (isWindowInsideSource(build(1))) {
      return 1;
    }
    let low = 0;
    let high = 1;
    for (let step = 0; step < CROP_CONTAINMENT_SEARCH_STEPS; step += 1) {
      const middle = (low + high) / 2;
      if (isWindowInsideSource(build(middle))) {
        low = middle;
      } else {
        high = middle;
      }
    }
    return low;
  }

  /** Fires after every canvas render while crop mode is open, so the overlay stays in step with every gesture. */
  private readonly onAfterRender = (): void => {
    this.#publishFrame();
  };

  /** Republishes both on-screen boxes, skipping the dispatch when nothing about them has moved. */
  #publishFrame(): void {
    if (!this.state) {
      this.lastPublishedFrames = null;
      getPosterUi().setMediaCropFrames(null);
      return;
    }
    const frames = this.#computeFrames();
    if (areFramesEqual(frames, this.lastPublishedFrames)) {
      return;
    }
    this.lastPublishedFrames = frames;
    getPosterUi().setMediaCropFrames(frames);
  }

  #computeFrames(): CropFrames {
    const state = this.state;
    if (!state) {
      const empty: CropFrame = {left: 0, top: 0, width: 0, height: 0, angle: 0};
      return {window: empty, image: empty};
    }
    return {window: getWindowFrame(state), image: getPictureFrame(state)};
  }

  /** Whether the crop, the picture's placement or its rotation differ from how they stood on entry. */
  #hasEditsSinceEntry(): boolean {
    if (!this.state || !this.entryFrames) {
      return false;
    }
    return !areFramesEqual(this.#computeFrames(), this.entryFrames, CROP_FRAME_EQUALITY_TOLERANCE);
  }

  /**
   * Leaves crop mode the way the user expects from a click beside the picture, the panel's back button or
   * dismissing the mobile sheet: keeping their edits, or simply closing when there are none - so an idle
   * exit never writes an undo entry.
   */
  public leaveCropMode(): void {
    if (this.#hasEditsSinceEntry()) {
      void this.applyCrop();
      return;
    }
    this.cancelCrop();
  }

  #teardownCanvasObjects(page: Page): void {
    const {windowRect, pictureImage, scrimRect} = this;
    if (windowRect) {
      windowRect.off('moving', this.onWindowMoving);
    }
    for (const object of [pictureImage, scrimRect, windowRect]) {
      if (object) {
        page.fabricCanvas.remove(object);
      }
    }
    page.fabricCanvas.off('after:render', this.onAfterRender);
    if (page.fabricCanvas instanceof Canvas) {
      page.fabricCanvas.off('mouse:down', this.onCanvasMouseDown);
      page.fabricCanvas.off('mouse:move', this.onCanvasMouseMove);
      page.fabricCanvas.off('mouse:up', this.onCanvasMouseUp);
      page.fabricCanvas.selection = this.previousCanvasSelection;
    }
    this.isPanning = false;
    this.outsidePressClientPoint = null;
    page.activeSelection.clearSelection();
    page.renderAlignmentGuidelines.resume();
    page.poster.pages.setItemsInteractiveOnEveryPage(true);
  }

  #finishExit(page: Page, item: CropTargetItem): void {
    window.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    window.removeEventListener('pointerup', this.onDocumentPointerUp, true);
    window.removeEventListener('pointercancel', this.onDocumentPointerCancel, true);
    hotkeys.setScope(this.previousShortcutScope);
    const hiddenSourceObject = this.hiddenSourceObject;
    this.hiddenSourceObject = null;
    this.state = null;
    this.windowRect = null;
    this.windowClip = null;
    this.pictureImage = null;
    this.scrimRect = null;
    this.activeItem = null;
    this.aspectRatio = null;
    this.isOriginalAspectRatio = false;
    const wasWindowFixed = this.isWindowFixed;
    this.isWindowFixed = false;
    this.lastPublishedFrames = null;
    this.entryFrames = null;

    // Always re-show what was hidden on entry. An apply with no destructive change performs no re-init,
    // so the hidden object is still the live one; a re-init may instead have swapped in a fresh fabric
    // object, so the item's current object is restored as well.
    if (hiddenSourceObject) {
      hiddenSourceObject.visible = true;
    }
    const currentObject = this.#getCurrentFabricObject(page, item);
    if (currentObject) {
      currentObject.visible = true;
    }
    if (wasWindowFixed) {
      // A background off its timeline span was hidden before the crop; the blanket re-show above must not keep it up.
      page.items.getItem(item.uid)?.updateItemVisibility();
    }
    const selectableObject = this.#getSelectableFabricObjectAfterExit(page, item);
    if (selectableObject) {
      page.activeSelection.setActiveObject(selectableObject);
    }
    this.#releasePictureGesture(page);
    page.fabricCanvas.requestRenderAll();
    getPosterUi().exitMediaCropMode();
  }

  /** A slide is not in the page's item map - it lives in its slideshow's slide map. */
  #getCurrentFabricObject(page: Page, item: CropTargetItem): FabricObject | null {
    if (item.isMediaSlide()) {
      const slides = item.slideshow.slides.slidesHashMap;
      return item.uid in slides ? slides[item.uid].fabricObject : null;
    }
    return page.items.getItem(item.uid)?.fabricObject ?? null;
  }

  /** A slide cannot be the canvas' active object (it sits inside its slideshow group), so its slideshow is selected instead. */
  #getSelectableFabricObjectAfterExit(page: Page, item: CropTargetItem): FabricObject | null {
    const uid = item.isMediaSlide() ? item.slideshow.uid : item.uid;
    return page.items.getItem(uid)?.fabricObject ?? null;
  }

  /**
   * Fabric has moved the window rectangle; the picture must stay put, so the source point under the
   * new centre is looked up on the pinned picture and clamped so the window cannot slide off it, and
   * the rectangle is snapped back to wherever that clamp landed.
   */
  private readonly onWindowMoving = (): void => {
    if (this.windowRect) {
      this.#moveWindowCentreTo(this.windowRect.getCenterPoint());
    }
  };

  /** Moves the window so its centre lands on a scene point, as far as the picture allows; the picture stays put. */
  public moveWindowCentreTo(scenePoint: Point): void {
    if (this.isWindowFixed) {
      return;
    }
    this.#moveWindowCentreTo(scenePoint);
  }

  #moveWindowCentreTo(draggedCentre: Point): void {
    const state = this.state;
    if (!state) {
      return;
    }
    const dragged: StraightenCropState = {...state, windowCentreScene: draggedCentre, windowCentre: sceneToSource(state, draggedCentre)};
    const windowCentre = clampWindowCentre(dragged);
    this.state = {...dragged, windowCentre, windowCentreScene: sourceToScene(state, windowCentre)};
    this.#syncCanvasObjects();
  }

  /**
   * A press that misses every object (no `target`) either pans the picture under a fixed crop window,
   * when it lands on the picture, or - when it lands beside it - is remembered so its release can be
   * read as a click that leaves crop mode. When the window is the page it cannot be dragged, so a press
   * on it pans the picture too.
   */
  private readonly onCanvasMouseDown = (opt: TPointerEventInfo): void => {
    const state = this.state;
    if (!state || (opt.target && !this.#isPressOnFixedWindow(opt.target))) {
      return;
    }
    const pressed = sceneToSource(state, opt.scenePoint);
    if (pressed.x < 0 || pressed.x > state.sourceWidth || pressed.y < 0 || pressed.y > state.sourceHeight) {
      this.outsidePressClientPoint = getPointerClientPoint(opt.e);
      return;
    }
    this.isPanning = true;
    this.panStartScene = opt.scenePoint;
    this.panStartWindowCentre = state.windowCentre;
  };

  #isPressOnFixedWindow(target: FabricObject): boolean {
    return this.isWindowFixed && target === this.windowRect;
  }

  private readonly onCanvasMouseMove = (opt: TPointerEventInfo): void => {
    const state = this.state;
    if (!this.isPanning || !state) {
      return;
    }
    // The picture follows the pointer, so the source point under the fixed window moves the other way.
    const travelled = sceneVectorToSource(state, opt.scenePoint.subtract(this.panStartScene));
    const panned: StraightenCropState = {...state, windowCentre: this.panStartWindowCentre.subtract(travelled)};
    this.state = {...panned, windowCentre: clampWindowCentre(panned)};
    this.#syncCanvasObjects();
  };

  private readonly onCanvasMouseUp = (): void => {
    if (!this.isPanning) {
      return;
    }
    this.isPanning = false;
    const windowRect = this.windowRect;
    const page = this.activeItem?.page;
    if (windowRect && page) {
      page.activeSelection.setActiveObject(windowRect);
      page.fabricCanvas.requestRenderAll();
    }
  };

  /**
   * Anywhere on the screen outside the crop area - the cropped page's canvas, the on-canvas handles,
   * the side-panel options and the mobile sheet - a press is remembered so that its release, if it
   * has not travelled, leaves crop mode. The canvas itself sorts its own presses into pans and
   * beside-the-picture clicks.
   */
  private readonly onDocumentPointerDown = (e: PointerEvent): void => {
    if (!this.state || e.button !== 0 || this.#isInsideCropArea(e.target)) {
      return;
    }
    this.outsidePressClientPoint = getPointerClientPoint(e);
  };

  private readonly onDocumentPointerUp = (e: PointerEvent): void => {
    // A picture handle drag ends anywhere on the page, so its gesture is released here rather than on the canvas.
    if (this.activeItem) {
      this.#releasePictureGesture(this.activeItem.page);
    }
    const outsidePress = this.outsidePressClientPoint;
    this.outsidePressClientPoint = null;
    if (outsidePress && getPointerClientPoint(e).distanceFrom(outsidePress) <= CROP_OUTSIDE_CLICK_TOLERANCE_PX) {
      this.leaveCropMode();
    }
  };

  /** The browser took the pointer away mid-gesture (a system gesture, a second finger): nothing was released or clicked. */
  private readonly onDocumentPointerCancel = (): void => {
    this.outsidePressClientPoint = null;
    if (this.activeItem) {
      this.#releasePictureGesture(this.activeItem.page);
    }
  };

  #isInsideCropArea(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }
    const canvas = this.activeItem?.page.fabricCanvas;
    const isCroppedCanvas = canvas instanceof Canvas && target === canvas.upperCanvasEl;
    return isCroppedCanvas || target.closest(`[${CROP_SURFACE_ATTRIBUTE}]`) !== null;
  }

  #lockAspectRatio(lock: CropAspectLock): void {
    this.aspectRatio = lock.ratio;
    this.isOriginalAspectRatio = lock.isOriginal;
    if (this.activeItem) {
      this.lastAspectLockByItemUid.set(this.activeItem.uid, lock);
    }
    getPosterUi().setMediaCropAspectRatio(lock);
  }

  /**
   * Reopening an item's crop starts from the preset it was last cropped with in this session. A window
   * that already has that ratio - the applied crop - is kept as it stands; any other window is refitted
   * exactly as picking the preset would, so the preset shown is the ratio on the canvas.
   */
  #restoreRememberedAspectLock(item: CropTargetItem): void {
    const remembered = this.lastAspectLockByItemUid.get(item.uid);
    const state = this.state;
    if (!remembered || !state || this.isWindowFixed) {
      return;
    }
    if (remembered.ratio === null) {
      return;
    }
    if (this.#hasWindowRatio(state, remembered.ratio)) {
      this.#lockAspectRatio(remembered);
      return;
    }
    if (remembered.isOriginal) {
      this.resetToOriginal();
      return;
    }
    this.setAspectRatio(remembered.ratio);
  }

  #hasWindowRatio(state: StraightenCropState, ratio: number): boolean {
    if (state.windowHeight <= 0) {
      return false;
    }
    return Math.abs(state.windowWidth / state.windowHeight - ratio) <= ratio * CROP_ASPECT_RATIO_MATCH_TOLERANCE;
  }

  #trackApplyGA(): void {
    trackPosterBuilderGA4Events(GA4EventName.CROP_APPLIED, {[GA4EventParamName.VALUE]: this.#getAppliedPresetGAValue(), [GA4EventParamName.TYPE]: this.mediaType});
  }

  #getAppliedPresetGAValue(): string {
    if (this.isWindowFixed) {
      return CROP_GA_PRESET_PAGE;
    }
    if (this.isOriginalAspectRatio) {
      return CROP_GA_PRESET_ORIGINAL;
    }
    if (this.aspectRatio === null) {
      return CROP_GA_PRESET_FREE;
    }
    return CROP_GA_PRESET_BY_ASPECT_RATIO.get(this.aspectRatio) ?? CROP_GA_PRESET_FREE;
  }
}
